<template>
  <div class="ha-wrap">
    <div class="ha-kpis">
      <HrWorkforceKpiTile
        label="Average age"
        :value="averageAgeLabel"
        color="#1e293b"
        :note="onlyCommaFormat(recordedTotal) + ' of ' + onlyCommaFormat(total) + ' with a date of birth · ' + selectedBranchName"
      />
      <HrWorkforceKpiTile
        label="Largest age group"
        :value="largestRange.label"
        :color="largestRange.color"
        :pill="percentLabel(largestRange.value, total)"
        :note="onlyCommaFormat(largestRange.value) + ' staff'"
      />
      <HrWorkforceKpiTile
        label="Age 56 and above"
        :value="onlyCommaFormat(seniorCount)"
        :color="ageColors.ag_7"
        :pill="percentLabel(seniorCount, total)"
        note="of staff · 56–58, 59 and 60+"
      />
      <HrWorkforceKpiTile
        label="DOB missing"
        :value="onlyCommaFormat(missingCount)"
        :color="notSpecifiedColor"
        :pill="percentLabel(missingCount, total)"
        note="missing or invalid · click to list"
        clickable
        :active="filters.range === notSpecifiedKey"
        title="List employees without a valid date of birth"
        @select="applyChartFilter('range', notSpecifiedKey)"
      />
    </div>
    <HrWorkforceSubTabs :tabs="subTabs" :model-value="currentSubTab" @update:model-value="activeSubTab = $event" />
    <div v-if="currentSubTab === 'overview'" class="hw-split">
      <HrWorkforcePanel title="Staff by age range">
        <template #actions>
          <label v-if="hasGenderData" class="ha-toggle">
            <input v-model="splitByGender" type="checkbox"> Split by gender
          </label>
        </template>
        <HrWorkforceLegend v-if="splitByGender" :items="genderSeries" />
        <BarChart :chartData="chartData" :chartOptions="chartOptions" :plugins="chartPlugins" :styles="chartStyles" chart-id="hr-age-chart" />
        <p class="hw-muted ha-chart-hint">click a column to list those employees · ages as of last Sync</p>
      </HrWorkforcePanel>
      <HrWorkforcePanel title="Age ranges" :note="onlyCommaFormat(recordedTotal) + ' of ' + onlyCommaFormat(total) + ' recorded'">
        <HrWorkforceBarRow
          v-for="item in rangeRows"
          :key="item.key"
          :dot="item.color"
          :name="item.label"
          :segments="[item]"
          :scale="maxRangeValue"
          :value="onlyCommaFormat(item.value)"
          :pill="percentLabel(item.value, total)"
          name-width="60px"
          clickable
          :active="filters.range === item.key"
          :muted="item.value === 0"
          :disabled="item.value === 0"
          @select="applyChartFilter('range', item.key)"
        />
      </HrWorkforcePanel>
    </div>
    <HrWorkforcePanel v-if="currentSubTab === 'branches'" title="Age mix by branch" note="largest first · click a branch to drill down">
      <HrWorkforceLegend :items="rangeLegend" />
      <div class="hw-scroll">
        <HrWorkforceBarRow
          v-for="row in branchAgeRows"
          :key="row.key"
          :name="row.name"
          :title="stackTitle(row)"
          :segments="row.parts"
          :scale="row.total"
          stacked
          :value="onlyCommaFormat(row.total)"
          :pill="row.average === null ? '–' : row.average + ' y'"
          name-width="180px"
          clickable
          @select="selectBranch(row.key)"
        />
      </div>
    </HrWorkforcePanel>
    <HrWorkforcePanel
      v-if="currentSubTab === 'employees'"
      class="ha-employees"
      title="Employees"
      :note="'up to ' + perBucketLimit + ' per branch and age range · ages as of today, ranges as of last Sync'"
      :pager="filteredEmployees.length > 0 ? pager : null"
      @update:page="page = $event"
    >
      <HrWorkforceEmployeeList
        :rows="pagedEmployees"
        :search="searchText"
        :chips="activeChips"
        :capped-note="cappedNote"
        :empty-text="emptyText"
        :action-label="employeePage ? 'Open profile' : ''"
        @search="onSearch"
        @clear="clearChip"
        @clear-all="clearAll"
        @open="openProfile"
      >
        <template #filters>
          <select v-model="filters.range" class="form-select form-select-sm" aria-label="Age range" @change="page = 1">
            <option value="">All age ranges ({{ onlyCommaFormat(employeeRows.length) }})</option>
            <option v-for="item in listRanges" :key="item.key" :value="item.key">{{ item.label }} ({{ onlyCommaFormat(item.value) }})</option>
          </select>
          <select v-if="listGenders.length > 0" v-model="filters.gender" class="form-select form-select-sm" aria-label="Gender" @change="page = 1">
            <option value="">Any gender</option>
            <option v-for="item in listGenders" :key="item.key" :value="item.key">{{ item.label }} ({{ onlyCommaFormat(item.value) }})</option>
          </select>
        </template>
        <template #default="{ employee }">
          <div class="ha-emp-age">
            <div v-if="employee.age !== null"><strong class="ha-age-value">{{ employee.age }}</strong><span class="hw-muted"> years</span></div>
            <div v-if="employee.age !== null" class="hw-muted">born {{ formatToDdMmmYyyy(employee.dob) }}</div>
            <strong v-if="employee.age === null" class="ha-age-missing">{{ employee.dob ? 'Invalid DOB' : 'No DOB' }}</strong>
            <div v-if="employee.age === null && employee.dob" class="hw-muted hw-ellipsis" :title="employee.dob">{{ employee.dob }}</div>
          </div>
          <span class="ha-chip" :style="employee.avatar_style">{{ employee.range_label }}</span>
        </template>
      </HrWorkforceEmployeeList>
      <template #pager-note>
        <span v-if="isCapped"> · <strong class="hw-true-total">{{ onlyCommaFormat(trueMatchTotal) }}</strong> match in total</span>
      </template>
    </HrWorkforcePanel>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import BarChart from '@/components/atom/chart/barChart'
import figureFormatter from '@/services/utils/figureFormatter'
import useDate from '@/services/utils/day'
import { ageColors, genderColors, longListColor, matchesSearch, notAssignedKey, notSpecifiedColor, notSpecifiedKey, percentLabel, useHrWorkforceEmployeeFilters, useHrWorkforceEmployeeProfile, useHrWorkforcePagedRows } from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/hrWorkforce'
import HrWorkforcePanel from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforcePanel.vue'
import HrWorkforceKpiTile from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceKpiTile.vue'
import HrWorkforceSubTabs from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceSubTabs.vue'
import HrWorkforceLegend from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceLegend.vue'
import HrWorkforceBarRow from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceBarRow.vue'
import HrWorkforceEmployeeList from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceEmployeeList.vue'

const props = defineProps({
  current: { type: Object, required: true },
  labels: { type: Object, required: true },
  branchRows: { type: Array, required: true },
  selectedBranch: { type: String, required: true },
  selectedBranchName: { type: String, required: true },
  ageEmployees: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['select-branch'])

const rangeOrder = ['ag_1', 'ag_2', 'ag_3', 'ag_4', 'ag_5', 'ag_6', 'ag_7', 'ag_8']
const seniorRanges = ['ag_6', 'ag_7', 'ag_8']
const chartKeys = [...rangeOrder, notSpecifiedKey]
const mutedAlpha = '55'
const tintAlpha = '26'
const perBucketLimit = 10
const pageSize = 10
const chartStyles = { position: 'relative', height: '280px' }

const { onlyCommaFormat } = figureFormatter()
const { formatToDdMmmYyyy, ageInYears } = useDate()
const { employeePage, openProfile } = useHrWorkforceEmployeeProfile()

const splitByGender = ref(false)
const filters = reactive({ range: '', gender: '', search: '' })
const { activeSubTab, searchText, page, applyChartFilter, selectBranch, clearChip, clearAll, onSearch } = useHrWorkforceEmployeeFilters(filters, props, emit)

const rangeLabels = computed(() => props.labels.age_ranges || {})
const genderLabels = computed(() => props.labels.genders || {})
const ageCounts = computed(() => props.current.age || {})
const ageGenderCounts = computed(() => props.current.age_gender || {})
const total = computed(() => props.current.total || 0)
const missingCount = computed(() => ageCounts.value[notSpecifiedKey] || 0)
const recordedTotal = computed(() => rangeOrder.reduce((sum, key) => sum + (ageCounts.value[key] || 0), 0))
const averageAge = computed(() => recordedTotal.value > 0 ? Math.round((props.current.age_years_sum || 0) * 10 / recordedTotal.value) / 10 : null)
const averageAgeLabel = computed(() => averageAge.value === null ? '–' : averageAge.value + ' y')

const rangeRows = computed(() => rangeOrder.map(key => ({ key, label: rangeLabelOf(key), value: ageCounts.value[key] || 0, color: ageColors[key] })))
const rangeLegend = computed(() => [...rangeRows.value, { key: notSpecifiedKey, label: 'DOB missing', value: missingCount.value, color: notSpecifiedColor }].filter(item => item.value > 0))
const maxRangeValue = computed(() => Math.max(1, ...rangeRows.value.map(item => item.value)))
const largestRange = computed(() => rangeRows.value.reduce((best, item) => item.value > best.value ? item : best, { key: '', label: '–', value: 0, color: '#1e293b' }))
const seniorCount = computed(() => seniorRanges.reduce((sum, key) => sum + (ageCounts.value[key] || 0), 0))

const genderKeys = computed(() => Object.keys(genderLabels.value))
const genderSeries = computed(() => [
  ...genderKeys.value.map(key => ({ key, label: genderLabels.value[key], color: genderColors[key] || longListColor })),
  { key: notSpecifiedKey, label: 'Gender not specified', color: notSpecifiedColor }
].filter(series => chartKeys.some(rangeKey => genderCount(rangeKey, series.key) > 0)))
const hasGenderData = computed(() => genderSeries.value.some(series => series.key !== notSpecifiedKey))

const singleDataset = computed(() => ({
  label: 'Staff',
  data: chartKeys.map(key => ageCounts.value[key] || 0),
  backgroundColor: chartKeys.map(key => columnColor(key, key === notSpecifiedKey ? notSpecifiedColor : ageColors[key])),
  borderRadius: 6,
  maxBarThickness: 48
}))
const genderDatasets = computed(() => genderSeries.value.map(series => ({
  label: series.label,
  data: chartKeys.map(key => genderCount(key, series.key)),
  backgroundColor: chartKeys.map(key => columnColor(key, series.color)),
  borderRadius: 3,
  maxBarThickness: 48
})))
const chartData = computed(() => ({
  labels: [...rangeRows.value.map(item => item.label), 'DOB missing'],
  datasets: splitByGender.value ? genderDatasets.value : [singleDataset.value]
}))

const branchAgeRows = computed(() => props.branchRows
  .map(row => branchAgeRow(row))
  .filter(row => row.total > 0)
  .sort((a, b) => (a.key === notAssignedKey) - (b.key === notAssignedKey) || b.total - a.total))

const subTabs = computed(() => [
  { key: 'overview', title: 'Overview', count: null, visible: true },
  { key: 'branches', title: 'By branch', count: branchAgeRows.value.length, visible: props.selectedBranch === 'all' },
  { key: 'employees', title: 'Employees', count: filteredEmployees.value.length, visible: true }
].filter(tab => tab.visible))
const currentSubTab = computed(() => subTabs.value.some(tab => tab.key === activeSubTab.value) ? activeSubTab.value : subTabs.value[0].key)

const employeeRows = computed(() => {
  const map = props.ageEmployees || {}
  return Object.keys(map)
    .filter(bucketKey => props.selectedBranch === 'all' || bucketKey.split('|')[0] === props.selectedBranch)
    .flatMap(bucketKey => (map[bucketKey] || []).map(row => toEmployee(row, bucketKey)))
    .sort((a, b) => a.range_index - b.range_index || (b.age || 0) - (a.age || 0) || String(a.name).localeCompare(String(b.name)))
})

const filteredEmployees = computed(() => {
  const search = filters.search.toLowerCase()
  return employeeRows.value.filter(row => matchesFilters(row, search))
})

const listRanges = computed(() => chartKeys
  .map(key => ({ key, label: rangeLabelOf(key), value: employeeRows.value.filter(row => row.range_key === key).length }))
  .filter(item => item.value > 0 || filters.range === item.key))

const listGenders = computed(() => [
  ...genderKeys.value.map(key => ({ key, label: genderLabels.value[key] })),
  { key: notSpecifiedKey, label: 'Gender not specified' }
].map(item => ({ ...item, value: employeeRows.value.filter(row => row.gender_key === item.key).length }))
  .filter(item => item.value > 0 || filters.gender === item.key))

const trueMatchTotal = computed(() => {
  if (filters.search !== '') return null
  if (filters.range !== '' && filters.gender !== '') return genderCount(filters.range, filters.gender)
  if (filters.range !== '') return ageCounts.value[filters.range] || 0
  if (filters.gender !== '') return chartKeys.reduce((sum, key) => sum + genderCount(key, filters.gender), 0)
  return total.value
})

const isCapped = computed(() => trueMatchTotal.value !== null && trueMatchTotal.value > filteredEmployees.value.length)

const { pagedRows: pagedEmployees, pager } = useHrWorkforcePagedRows(filteredEmployees, page, pageSize)

const cappedNote = computed(() => {
  if (!isCapped.value) return ''
  return onlyCommaFormat(trueMatchTotal.value) + ' employees match in total. Only the first ' + perBucketLimit + ' per branch and age range are kept at Sync, so ' + onlyCommaFormat(trueMatchTotal.value - filteredEmployees.value.length) + ' more are not listed.'
})

const emptyText = computed(() => {
  if (employeeRows.value.length === 0) return 'No employees in this selection.'
  if (filteredEmployees.value.length === 0) return 'No employees match these filters.'
  return ''
})

const activeChips = computed(() => {
  const chips = []
  if (props.selectedBranch !== 'all') chips.push({ key: 'branch', label: 'Branch: ' + props.selectedBranchName })
  if (filters.range !== '') chips.push({ key: 'range', label: 'Age: ' + rangeLabelOf(filters.range) })
  if (filters.gender !== '') chips.push({ key: 'gender', label: 'Gender: ' + (genderLabels.value[filters.gender] || 'Not specified') })
  if (filters.search !== '') chips.push({ key: 'search', label: 'Search: ' + filters.search })
  return chips
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 22 } },
  onClick: (event, elements) => {
    if (elements.length === 0) return
    applyChartFilter('range', chartKeys[elements[0].index])
  },
  onHover: (event, elements) => {
    if (!event.native) return
    event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
  },
  scales: {
    x: { stacked: true, grid: { display: false }, ticks: { autoSkip: false, maxRotation: 0, font: { size: 11 }, color: '#64748b' } },
    y: { stacked: true, beginAtZero: true, ticks: { precision: 0, font: { size: 11 }, color: '#64748b' }, grid: { color: '#f1f5f9', borderDash: [3, 3] } }
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) => context.dataset.label + ': ' + onlyCommaFormat(context.parsed.y) + ' (' + percentLabel(context.parsed.y, total.value) + ')',
        footer: (items) => splitByGender.value && items.length > 0 ? 'Total ' + onlyCommaFormat(columnTotal(items[0].dataIndex)) : ''
      }
    }
  }
}

const chartPlugins = [{
  id: 'hrAgeColumnLabels',
  afterDatasetsDraw: (chart) => {
    const meta = chart.getDatasetMeta(0)
    if (!meta || meta.data.length === 0) return
    const compact = chart.width < 560
    const ctx = chart.ctx
    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.fillStyle = '#334155'
    ctx.font = '600 11px sans-serif'
    chartKeys.forEach((key, index) => {
      const value = columnTotal(index)
      if (value === 0 || !meta.data[index]) return
      const label = compact ? onlyCommaFormat(value) : onlyCommaFormat(value) + ' · ' + percentLabel(value, total.value)
      ctx.fillText(label, meta.data[index].x, chart.scales.y.getPixelForValue(value) - 4)
    })
    ctx.restore()
  }
}]

const rangeLabelOf = (key) => key === notSpecifiedKey ? 'DOB missing' : rangeLabels.value[key] || key

const genderCount = (rangeKey, genderKey) => ageGenderCounts.value[rangeKey + '|' + genderKey] || 0

const columnTotal = (index) => ageCounts.value[chartKeys[index]] || 0

const columnColor = (key, color) => filters.range !== '' && filters.range !== key ? color + mutedAlpha : color

const branchAgeRow = (row) => {
  const counts = row.summary.age || {}
  const recorded = rangeOrder.reduce((sum, key) => sum + (counts[key] || 0), 0)
  const parts = [
    ...rangeOrder.map(key => ({ key, label: rangeLabelOf(key), value: counts[key] || 0, color: ageColors[key] })),
    { key: notSpecifiedKey, label: 'DOB missing', value: counts[notSpecifiedKey] || 0, color: notSpecifiedColor }
  ].filter(part => part.value > 0)
  return {
    key: row.key,
    name: row.name,
    total: row.summary.total || 0,
    average: recorded > 0 ? Math.round((row.summary.age_years_sum || 0) / recorded) : null,
    parts
  }
}

const stackTitle = (row) => row.name + ' · ' + row.parts.map(part => part.label + ' ' + part.value).join(' · ') + (row.average === null ? '' : ' · average ' + row.average + ' y')

const toEmployee = (row, bucketKey) => {
  const [branchKey, rangeKey] = bucketKey.split('|')
  const isMissing = rangeKey === notSpecifiedKey
  const branchName = branchKey === notAssignedKey ? '' : (props.labels.branches || {})[branchKey] || ''
  const genderLabel = genderLabels.value[row.gender] || ''
  return {
    ...row,
    branch_key: branchKey,
    range_key: rangeKey,
    range_index: isMissing ? rangeOrder.length : rangeOrder.indexOf(rangeKey),
    range_label: rangeLabelOf(rangeKey),
    gender_key: genderLabels.value[row.gender] ? row.gender : notSpecifiedKey,
    age: isMissing ? null : ageInYears(row.dob),
    subtitle: (row.serial ? 'ID ' + row.serial + ' · ' : '') + (branchName || 'No branch') + (genderLabel ? ' · ' + genderLabel : ''),
    avatar_style: { backgroundColor: isMissing ? '#f1f5f9' : ageColors[rangeKey] + tintAlpha, color: isMissing ? '#64748b' : '#134e4a' }
  }
}

const matchesFilters = (row, search) => {
  if (filters.range !== '' && row.range_key !== filters.range) return false
  if (filters.gender !== '' && row.gender_key !== filters.gender) return false
  return matchesSearch(row, search)
}
</script>

<style scoped src="./common/hrWorkforce.css"></style>

<style scoped>
.ha-wrap {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.ha-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}
.ha-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  margin: 0;
}
.ha-chart-hint {
  margin: 8px 0 0;
  text-align: center;
}
.ha-employees {
  --hw-filter-cols: minmax(200px, 1.6fr) repeat(2, minmax(150px, 1fr));
  --hw-emp-cols: minmax(0, 1.6fr) minmax(130px, 1fr) auto auto;
}
.ha-emp-age {
  min-width: 0;
  line-height: 1.3;
}
.ha-age-value {
  font-size: 1.1rem;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}
.ha-age-missing {
  font-size: 0.85rem;
  color: #b42318;
}
.ha-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
@media (max-width: 1199px) {
  .ha-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 991px) {
  .ha-employees {
    --hw-emp-cols: minmax(0, 1fr) minmax(130px, 1fr) auto;
  }
  .ha-chip {
    grid-column: 1 / -1;
    justify-self: start;
  }
}
</style>
